import { useEnvVarVersions } from '@/api/hooks';
import type { EnvVar, EnvVarVersion } from '@/api/schemas';
import { formatBytes, formatDateTime } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Tag, VisibilityBadge } from '@/components/badges';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { lineCountLabel, listView } from './envHelpers';
import { versionAuthor } from './versionModel';

/** Version history (screen 06 "Audit trail & version history"): vN, who, when, note, fingerprint, size — never values. */
export function VersionsSheet({ envVar, onOpenChange }: { envVar: EnvVar | null; onOpenChange: (o: boolean) => void }) {
  return (
    <Sheet open={Boolean(envVar)} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="env-versions-sheet" className="w-full gap-0 p-0" aria-describedby="env-versions-desc">
        {envVar ? <VersionsContent envVar={envVar} /> : null}
      </SheetContent>
    </Sheet>
  );
}

function VersionsContent({ envVar }: { envVar: EnvVar }) {
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex flex-wrap items-center gap-2">
          <span className="font-mono">{envVar.key}</span>
          <VisibilityBadge visibility={envVar.visibility} />
        </SheetTitle>
        <SheetDescription id="env-versions-desc" className="text-xs">
          Immutable versions, newest first. Equal fingerprints mean equal values.
        </SheetDescription>
      </SheetHeader>
      <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
        <VersionsList envVarId={envVar.id} />
      </div>
    </>
  );
}

function VersionsList({ envVarId }: { envVarId: string }) {
  const versions = useEnvVarVersions(envVarId);
  const view = listView({ isLoading: versions.isLoading, isError: versions.isError, hasData: Boolean(versions.data), count: versions.data?.length ?? 0 });
  if (view === 'loading') return <VersionsSkeleton />;
  if (view === 'error') return <ErrorState compact error={versions.error} onRetry={() => void versions.refetch()} />;
  if (view === 'empty' || !versions.data) return <EmptyState compact icon="history" title="No versions" />;
  return (
    <ol className="relative flex flex-col gap-3 border-l pl-5">
      {versions.data.map((v, i) => (
        <VersionItem key={v.id} v={v} i={i} />
      ))}
    </ol>
  );
}

function VersionsSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  );
}

function VersionItem({ v, i }: { v: EnvVarVersion; i: number }) {
  return (
    <li data-testid={`env-version-${v.version}`} style={stagger(i)} className="enter relative">
      <span className={cn('absolute -left-[29px] top-1 flex size-4 items-center justify-center rounded-full border text-[9px] font-semibold', v.current ? 'border-primary bg-primary text-primary-foreground' : 'bg-card text-subtle')}>{v.version}</span>
      <div className="flex flex-wrap items-center gap-2 text-[13px]">
        <span className="font-medium text-foreground">v{v.version}</span>
        {v.current ? <Tag className="text-primary">current</Tag> : null}
        {v.pendingApprovalId ? <Tag className="text-awaiting">pending approval</Tag> : null}
        <span className="ml-auto font-mono text-[11px] text-subtle">{formatDateTime(v.createdAt)}</span>
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">{versionAuthor(v.createdBy)}</div>
      {v.note ? <p className="mt-1 text-[13px] italic text-muted-foreground">“{v.note}”</p> : null}
      <VersionFacts v={v} />
    </li>
  );
}

function VersionFacts({ v }: { v: EnvVarVersion }) {
  return (
    <div className="mt-1 flex flex-wrap gap-x-3 font-mono text-[11px] text-subtle">
      <span>{v.valueFingerprint}</span>
      <span>{formatBytes(v.byteLength)}</span>
      <span>{lineCountLabel(v.lineCount)}</span>
      {v.preview !== null && v.preview !== undefined ? <span className="text-muted-foreground">{v.preview}</span> : null}
    </div>
  );
}
